import { type KeyboardEvent, type ReactNode, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  AlertCircle,
  ChevronLeft,
  ChevronRight,
  GitBranch,
  Github,
  Globe,
  HardDrive,
  Network,
  Plus,
  Search,
  Upload,
} from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { apiClient } from '@/lib/api-client'
import { cn } from '@/lib/utils'
import type { Network as NetworkType, NetworkSourceType } from '@/types/network'

type FilterValue = 'all' | 'active' | 'inactive' | 'needs-validation'
type SortValue = 'last-validated' | 'recently-added' | 'name'

type SourceMeta = {
  icon: typeof Github
  label: string
}

type StatusSummary = {
  label: string
  detail: string
  dotClassName: string
  badgeClassName: string
}

const PAGE_SIZE = 4

const sourceTypeMeta: Record<NetworkSourceType, SourceMeta> = {
  github: { icon: Github, label: 'GitHub' },
  gitlab: { icon: GitBranch, label: 'GitLab' },
  direct: { icon: Upload, label: 'Direct upload' },
  cli: { icon: Upload, label: 'CLI push' },
  http: { icon: Globe, label: 'HTTP endpoint' },
  local: { icon: HardDrive, label: 'Local source' },
}

const sourceFilters: Array<{ value: 'all' | NetworkSourceType; label: string }> = [
  { value: 'all', label: 'All' },
  { value: 'github', label: 'GitHub' },
  { value: 'gitlab', label: 'GitLab' },
  { value: 'direct', label: 'Direct upload' },
  { value: 'cli', label: 'CLI push' },
  { value: 'http', label: 'HTTP' },
  { value: 'local', label: 'Local' },
]

const statusFilters: Array<{ value: FilterValue; label: string }> = [
  { value: 'all', label: 'All' },
  { value: 'active', label: 'Active' },
  { value: 'inactive', label: 'Inactive' },
  { value: 'needs-validation', label: 'Needs validation' },
]

const sortOptions: Array<{ value: SortValue; label: string }> = [
  { value: 'last-validated', label: 'Last updated' },
  { value: 'recently-added', label: 'Recently added' },
  { value: 'name', label: 'Name' },
]

function formatDateTime(value?: string) {
  if (!value) {
    return 'Unknown'
  }

  const date = new Date(value)
  if (Number.isNaN(date.getTime())) {
    return 'Unknown'
  }

  return date.toLocaleString()
}

function formatRelativeTime(value?: string) {
  if (!value) {
    return 'Awaiting first validation'
  }

  const date = new Date(value)
  if (Number.isNaN(date.getTime())) {
    return 'Awaiting first validation'
  }

  const diffMs = date.getTime() - Date.now()
  const diffMinutes = Math.round(diffMs / (1000 * 60))
  const formatter = new Intl.RelativeTimeFormat(undefined, { numeric: 'auto' })

  if (Math.abs(diffMinutes) < 60) {
    return formatter.format(diffMinutes, 'minute')
  }

  const diffHours = Math.round(diffMinutes / 60)
  if (Math.abs(diffHours) < 48) {
    return formatter.format(diffHours, 'hour')
  }

  return formatter.format(Math.round(diffHours / 24), 'day')
}

function handleRowKeyDown(event: KeyboardEvent<HTMLElement>, onSelect: () => void) {
  if (event.key === 'Enter' || event.key === ' ') {
    event.preventDefault()
    onSelect()
  }
}

function getSourceSummary(network: NetworkType) {
  const meta = sourceTypeMeta[network.sourceType] ?? {
    icon: Network,
    label: network.sourceType,
  }
  const sourceConfig = network.sourceConfig as Record<string, unknown>

  if (network.sourceType === 'github' || network.sourceType === 'gitlab') {
    const visibility = typeof sourceConfig.private === 'boolean'
      ? (sourceConfig.private ? 'Private' : 'Public')
      : undefined

    return {
      meta,
      primary: typeof sourceConfig.fullName === 'string' ? sourceConfig.fullName : network.name,
      secondary: network.defaultBranch ? `Branch: ${network.defaultBranch}` : 'Branch not set',
      visibility,
    }
  }

  if (network.sourceType === 'http') {
    return {
      meta,
      primary: typeof sourceConfig.url === 'string' ? sourceConfig.url : 'Remote endpoint',
      secondary: network.defaultBranch ? `Mode: ${network.defaultBranch}` : 'Remote endpoint',
      visibility: undefined,
    }
  }

  if (network.sourceType === 'local') {
    return {
      meta,
      primary: typeof sourceConfig.path === 'string' ? sourceConfig.path : 'Local source',
      secondary: network.defaultBranch ? `Snapshot: ${network.defaultBranch}` : 'Filesystem source',
      visibility: undefined,
    }
  }

  if (network.sourceType === 'direct') {
    const count = typeof sourceConfig.configCount === 'number' ? sourceConfig.configCount : null
    return {
      meta,
      primary: count === null ? 'Uploaded configuration set' : `${count} configs uploaded`,
      secondary: 'Stored in EtherPilot',
      visibility: undefined,
    }
  }

  return {
    meta,
    primary: 'CLI submitted source',
    secondary: network.defaultBranch ? `Branch: ${network.defaultBranch}` : 'CLI-managed source',
    visibility: undefined,
  }
}

function getStatusSummary(network: NetworkType): StatusSummary {
  if (!network.active) {
    return {
      label: 'Inactive',
      detail: 'Validation paused',
      dotClassName: 'bg-slate-500',
      badgeClassName: 'border-slate-200 bg-slate-100 text-slate-700',
    }
  }

  if (!network.lastValidatedAt) {
    return {
      label: 'Needs validation',
      detail: 'No completed validation yet',
      dotClassName: 'bg-amber-500',
      badgeClassName: 'border-amber-200 bg-amber-50 text-amber-700',
    }
  }

  const validatedAt = new Date(network.lastValidatedAt).getTime()
  const ageMs = Date.now() - validatedAt
  if (Number.isNaN(validatedAt) || ageMs > 1000 * 60 * 60 * 24 * 7) {
    return {
      label: 'Validation stale',
      detail: 'Last validation is older than one week',
      dotClassName: 'bg-amber-600',
      badgeClassName: 'border-amber-200 bg-amber-50 text-amber-700',
    }
  }

  return {
    label: 'Active',
    detail: 'Recent validation history available',
    dotClassName: 'bg-primary',
    badgeClassName: 'border-blue-200 bg-blue-50 text-blue-700',
  }
}

function matchesStatus(network: NetworkType, filterValue: FilterValue) {
  if (filterValue === 'all') {
    return true
  }

  if (filterValue === 'active') {
    return network.active
  }

  if (filterValue === 'inactive') {
    return !network.active
  }

  return network.active && !network.lastValidatedAt
}

function EmptyState({
  title,
  description,
  action,
}: {
  title: string
  description: string
  action?: ReactNode
}) {
  return (
    <div className="rounded-lg border border-dashed border-border/50 bg-background px-5 py-12 text-center">
      <h3 className="text-lg font-semibold text-foreground">{title}</h3>
      <p className="mx-auto mt-2 max-w-2xl text-sm leading-6 text-muted-foreground">{description}</p>
      {action ? <div className="mt-6">{action}</div> : null}
    </div>
  )
}

export default function NetworksPage() {
  const navigate = useNavigate()
  const [networks, setNetworks] = useState<NetworkType[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [searchValue, setSearchValue] = useState('')
  const [statusFilter, setStatusFilter] = useState<FilterValue>('all')
  const [sourceFilter, setSourceFilter] = useState<'all' | NetworkSourceType>('all')
  const [sortValue, setSortValue] = useState<SortValue>('last-validated')
  const [page, setPage] = useState(1)

  useEffect(() => {
    loadNetworks()
  }, [])

  useEffect(() => {
    setPage(1)
  }, [searchValue, sourceFilter, sortValue, statusFilter])

  const loadNetworks = async () => {
    try {
      setIsLoading(true)
      setError(null)
      const response = await apiClient.getNetworks()

      if (response.success && response.data) {
        setNetworks(response.data.networks || [])
      } else {
        setError(response.error || 'Failed to load networks')
      }
    } catch (err) {
      setError('Failed to load networks')
      console.error('Error loading networks:', err)
    } finally {
      setIsLoading(false)
    }
  }

  const resetFilters = () => {
    setSearchValue('')
    setStatusFilter('all')
    setSourceFilter('all')
    setSortValue('last-validated')
  }

  const searchTerm = searchValue.trim().toLowerCase()

  const filteredNetworks = networks
    .filter((network) => matchesStatus(network, statusFilter))
    .filter((network) => sourceFilter === 'all' || network.sourceType === sourceFilter)
    .filter((network) => {
      if (!searchTerm) {
        return true
      }

      const source = getSourceSummary(network)
      const searchable = [
        network.name,
        network.description,
        source.primary,
        source.secondary,
        source.meta.label,
      ].filter(Boolean).join(' ')

      return searchable.toLowerCase().includes(searchTerm)
    })
    .sort((left, right) => {
      if (sortValue === 'name') {
        return left.name.localeCompare(right.name)
      }

      if (sortValue === 'recently-added') {
        return new Date(right.createdAt).getTime() - new Date(left.createdAt).getTime()
      }

      return new Date(right.lastValidatedAt ?? right.updatedAt).getTime() - new Date(left.lastValidatedAt ?? left.updatedAt).getTime()
    })

  const filtersApplied =
    searchValue.length > 0 ||
    statusFilter !== 'all' ||
    sourceFilter !== 'all' ||
    sortValue !== 'last-validated'

  const totalPages = Math.max(1, Math.ceil(filteredNetworks.length / PAGE_SIZE))
  const currentPage = Math.min(page, totalPages)
  const pageStart = (currentPage - 1) * PAGE_SIZE
  const paginatedNetworks = filteredNetworks.slice(pageStart, pageStart + PAGE_SIZE)
  const pageNumbers = Array.from({ length: totalPages }, (_, index) => index + 1)

  return (
    <div className="mx-auto flex w-full max-w-6xl flex-col gap-4">
      <section className="flex flex-col gap-4 border-b border-border pb-4">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="min-w-0">
            <h1 className="text-[1.85rem] font-bold tracking-[-0.04em] text-foreground">Networks</h1>
            <p className="mt-1 max-w-3xl text-sm text-muted-foreground">
              Scan registered networks, source coverage, and validation recency from one inventory.
            </p>
          </div>
          <Button
            className="h-10 rounded-md border border-primary/20 bg-primary px-4 text-sm font-semibold hover:bg-primary/95"
            onClick={() => navigate('/networks/add')}
          >
            <Plus className="mr-2 h-4 w-4" />
            New Network
          </Button>
        </div>

        <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
          <div className="relative min-w-0 flex-1">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              aria-label="Search networks"
              value={searchValue}
              onChange={(event) => setSearchValue(event.target.value)}
              placeholder="Find a network..."
              className="h-9 rounded-md border-border bg-muted pl-10"
            />
          </div>
          <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center sm:justify-end">
            <Select value={sourceFilter} onValueChange={(value) => setSourceFilter(value as 'all' | NetworkSourceType)}>
              <SelectTrigger aria-label="Filter networks by source type" className="h-9 min-w-[9rem] rounded-md border-border bg-muted text-xs font-medium">
                <SelectValue placeholder="Type: All" />
              </SelectTrigger>
              <SelectContent>
                {sourceFilters.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {`Type: ${option.label}`}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={statusFilter} onValueChange={(value) => setStatusFilter(value as FilterValue)}>
              <SelectTrigger aria-label="Filter networks by validation status" className="h-9 min-w-[10rem] rounded-md border-border bg-muted text-xs font-medium">
                <SelectValue placeholder="Status: All" />
              </SelectTrigger>
              <SelectContent>
                {statusFilters.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {`Status: ${option.label}`}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={sortValue} onValueChange={(value) => setSortValue(value as SortValue)}>
              <SelectTrigger aria-label="Sort networks" className="h-9 min-w-[10rem] rounded-md border-border bg-muted text-xs font-medium">
                <SelectValue placeholder="Sort: Last updated" />
              </SelectTrigger>
              <SelectContent>
                {sortOptions.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {`Sort: ${option.label}`}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {filtersApplied ? (
              <Button
                type="button"
                variant="ghost"
                className="h-9 rounded-md border border-border px-3 text-xs font-medium text-muted-foreground hover:bg-muted hover:text-foreground"
                onClick={resetFilters}
              >
                Reset filters
              </Button>
            ) : null}
          </div>
        </div>
      </section>

      {isLoading ? (
        <div className="flex min-h-[18rem] items-center justify-center rounded-lg border border-border bg-background">
          <Network className="h-8 w-8 animate-pulse text-primary" />
        </div>
      ) : error ? (
        <section className="rounded-lg border border-red-100 bg-red-50 px-5 py-4 text-sm text-red-700">
          <div className="flex items-center gap-2">
            <AlertCircle className="h-4 w-4" />
            <span>{error}</span>
          </div>
        </section>
      ) : networks.length === 0 ? (
        <EmptyState
          title="No networks yet"
          description="Add your first network source to start validating configurations and tracking results from this inventory."
          action={(
            <Button className="rounded-md border border-primary/20 bg-primary hover:bg-primary/95" onClick={() => navigate('/networks/add')}>
              <Plus className="mr-2 h-4 w-4" />
              Add Your First Network
            </Button>
          )}
        />
      ) : (
        <>
          <section className="overflow-hidden border-y border-border bg-background">
            {filteredNetworks.length === 0 ? (
              <div className="p-6">
                <EmptyState
                  title="No networks match the current filters."
                  description="Try a different search term or reset the current filters to widen the inventory."
                  action={filtersApplied ? (
                    <Button variant="outline" className="rounded-md" onClick={resetFilters}>
                      Reset filters
                    </Button>
                  ) : undefined}
                />
              </div>
            ) : (
              <div className="divide-y divide-[#dce3ea]">
                {paginatedNetworks.map((network) => {
                  const source = getSourceSummary(network)
                  const SourceIcon = source.meta.icon
                  const status = getStatusSummary(network)

                  return (
                    <div
                      key={network.id}
                      role="button"
                      tabIndex={0}
                      aria-label={network.name}
                      className="flex cursor-pointer flex-col gap-4 px-1 py-4 transition-colors hover:bg-muted/50 sm:px-2 lg:flex-row lg:items-start lg:justify-between lg:gap-6"
                      onClick={() => navigate(`/networks/${network.id}`)}
                      onKeyDown={(event) => handleRowKeyDown(event, () => navigate(`/networks/${network.id}`))}
                    >
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="break-words text-[1.05rem] font-bold leading-6 text-primary">
                            {network.name}
                          </span>
                          {source.visibility ? (
                            <Badge variant="outline" className="rounded-full border-border bg-background px-2 py-0 text-[10px] uppercase tracking-wide text-muted-foreground">
                              {source.visibility}
                            </Badge>
                          ) : null}
                          <Badge
                            variant="outline"
                            className={cn('rounded-full px-2 py-0 text-[10px] uppercase tracking-wide', status.badgeClassName)}
                          >
                            {status.label}
                          </Badge>
                        </div>

                        {network.description ? (
                          <p className="mt-1.5 max-w-3xl break-words text-sm leading-6 text-muted-foreground">
                            {network.description}
                          </p>
                        ) : (
                          <p className="mt-1.5 max-w-3xl break-words text-sm leading-6 text-muted-foreground">
                            {source.meta.label} inventory tracked in EtherPilot.
                          </p>
                        )}

                        <div className="mt-2.5 flex flex-wrap items-center gap-x-4 gap-y-2 text-xs text-muted-foreground">
                          <span className="flex min-w-0 items-center gap-1.5">
                            <span className={cn('h-2.5 w-2.5 shrink-0 rounded-full', status.dotClassName)} />
                            <span className="font-medium text-foreground">Source: {source.meta.label}</span>
                          </span>
                          <span className="flex min-w-0 items-center gap-1.5">
                            <SourceIcon className="h-3.5 w-3.5 shrink-0" />
                            <span className="break-all">{source.primary}</span>
                          </span>
                          <span className="break-words">{source.secondary}</span>
                          <span className="break-words">
                            Last validated: {network.lastValidatedAt ? formatRelativeTime(network.lastValidatedAt) : 'Awaiting first validation'}
                          </span>
                          <span className="break-words">
                            Added {formatRelativeTime(network.createdAt)}
                          </span>
                        </div>

                        <p className="mt-2 text-xs text-muted-foreground">
                          {status.detail}
                          <span className="ml-1 hidden sm:inline">
                            Updated {formatDateTime(network.updatedAt)}
                          </span>
                        </p>
                      </div>

                      <div className="flex items-center justify-between gap-4 lg:justify-end">
                        <div className="min-w-0 text-right text-[11px] text-muted-foreground lg:min-w-[8.5rem]">
                          <div className="font-medium text-foreground">
                            {network.lastValidatedAt ? 'Recent activity' : 'No activity yet'}
                          </div>
                          <div className="mt-1 break-words">
                            {network.lastValidatedAt ? formatDateTime(network.lastValidatedAt) : formatDateTime(network.createdAt)}
                          </div>
                        </div>
                      </div>
                    </div>
                  )
                })}
              </div>
            )}
          </section>

          {filteredNetworks.length > 0 ? (
            <nav aria-label="Networks pagination" className="flex items-center justify-center gap-1 pt-2 text-sm">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-8 rounded-md px-3 text-muted-foreground"
                onClick={() => setPage((value) => Math.max(1, value - 1))}
                disabled={currentPage === 1}
              >
                <ChevronLeft className="mr-1 h-3.5 w-3.5" />
                Previous
              </Button>
              {pageNumbers.map((pageNumber) => (
                <Button
                  key={pageNumber}
                  type="button"
                  variant="ghost"
                  size="sm"
                  className={cn(
                    'h-8 min-w-8 rounded-md px-2 text-sm',
                    currentPage === pageNumber
                      ? 'bg-primary text-white hover:bg-primary/95 hover:text-white'
                      : 'text-foreground',
                  )}
                  onClick={() => setPage(pageNumber)}
                >
                  {pageNumber}
                </Button>
              ))}
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-8 rounded-md px-3 text-muted-foreground"
                onClick={() => setPage((value) => Math.min(totalPages, value + 1))}
                disabled={currentPage === totalPages}
              >
                Next
                <ChevronRight className="ml-1 h-3.5 w-3.5" />
              </Button>
            </nav>
          ) : null}
        </>
      )}
    </div>
  )
}
